import React, { useState, useEffect, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { reportService, BillingReport } from '../../../services/reportService';
import apiClient from '../../../services/apiClient';
import { formatCurrency } from '../../../lib/format';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import EmptyState from '../../../components/ui/EmptyState';
import KpiCard from '../../../components/ui/KpiCard';

interface DashboardData {
  period: { start: string; end: string };
  admissions: { total: number; active: number; by_day: Array<{ date: string; count: number }>; by_status: any[] };
  patients: { total: number; new: number };
  consultations?: { total: number };
  appointments?: { total: number };
  laboratory: { total: number; completed: number };
  imaging: { total: number };
  financial: { revenue: number; discounts: number };
}

interface PatientReport {
  period: { start: string; end: string };
  summary: {
    total: number;
    new_in_period: number;
    by_gender: Array<{ label: string; count: number }>;
    by_month: Array<{ month: string; count: number }>;
  };
}

interface ConsultationReport {
  period: { start: string; end: string };
  total: number;
  by_doctor: Array<{ doctor_id: number; total: number; doctor: { id: number; first_name: string; last_name: string } | null }>;
  by_day: Array<{ date: string; count: number }>;
}

interface FinancialReport {
  period: { start: string; end: string };
  summary: {
    total_closures: number;
    services: number;
    medications: number;
    supplies: number;
    honoraries: number;
    discounts: number;
    returns: number;
    grand_total: number;
  };
  by_day: Array<{ date: string; total: number; count: number }>;
}

interface LabReport {
  period: { start: string; end: string };
  total_orders: number;
  by_status: Array<{ status: string; count: number }>;
  by_type: any[];
}

interface ImagingReport {
  period: { start: string; end: string };
  total_orders: number;
  by_status: Array<{ status: string; count: number }>;
  by_study: any[];
}

interface AdmissionReport {
  total: number;
  by_type: any[];
  by_priority: any[];
}

const TABS = [
  { key: 'general',      label: 'General',         icon: 'fa-dashboard' },
  { key: 'consultations', label: 'Consultas',       icon: 'fa-stethoscope' },
  { key: 'patients',     label: 'Pacientes',        icon: 'fa-users' },
  { key: 'financial',    label: 'Financiero',       icon: 'fa-money' },
  { key: 'admissions',   label: 'Hospitalización',  icon: 'fa-hospital' },
  { key: 'laboratory',   label: 'Laboratorio',      icon: 'fa-flask' },
  { key: 'imaging',      label: 'Imágenes',         icon: 'fa-image' },
  { key: 'billing',     label: 'Facturación',       icon: 'fa-credit-card' },
];

const STATUS_LABELS: Record<string, string> = {
  pending:   'Pendiente',
  processed: 'Procesado',
  validated: 'Validado',
  cancelled: 'Cancelado',
  completed: 'Completado',
  active:    'Activo',
  inactive:  'Inactivo',
};

const ReportsPage: React.FC = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [patientReport, setPatientReport] = useState<PatientReport | null>(null);
  const [consultationReport, setConsultationReport] = useState<ConsultationReport | null>(null);
  const [financialReport, setFinancialReport] = useState<FinancialReport | null>(null);
  const [labReport, setLabReport] = useState<LabReport | null>(null);
  const [imagingReport, setImagingReport] = useState<ImagingReport | null>(null);
  const [admissionReport, setAdmissionReport] = useState<AdmissionReport | null>(null);
  const [billingReport, setBillingReport] = useState<BillingReport | null>(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const q = `start_date=${startDate}&end_date=${endDate}`;
      const get = <T,>(url: string) =>
        apiClient.get<any>(`${url}?${q}`).then(r => r.data.data).catch(() => null);

      const [dash, patients, consults, financial, lab, imaging, admissions, billing] = await Promise.all([
        reportService.getDashboard({ start_date: startDate, end_date: endDate }).catch(() => null),
        get<PatientReport>('/reports/patients'),
        get<ConsultationReport>('/reports/consultations'),
        get<FinancialReport>('/reports/financial'),
        get<LabReport>('/reports/laboratory'),
        get<ImagingReport>('/reports/imaging'),
        get<AdmissionReport>('/reports/admissions'),
        get<BillingReport>('/reports/billing'),
      ]);

      setDashboard(dash);
      setPatientReport(patients);
      setConsultationReport(consults);
      setFinancialReport(financial);
      setLabReport(lab);
      setImagingReport(imaging);
      setAdmissionReport(admissions);
      setBillingReport(billing);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // ── Shared chart helpers ──────────────────────────────────────────────────

  const areaOptions = (categories: string[], tooltipSuffix = '') => ({
    chart: { type: 'area' as const, height: 300, toolbar: { show: false } },
    colors: ['#2E37A4'],
    stroke: { curve: 'smooth' as const, width: 2 },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.1 } },
    xaxis: { categories },
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (v: number) => `${v}${tooltipSuffix}` } },
  });

  const barOptions = (categories: string[], color = '#00e396', horizontal = false) => ({
    chart: { type: 'bar' as const, height: 300, toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 6, horizontal } },
    colors: [color],
    xaxis: { categories },
    dataLabels: { enabled: true },
  });

  const donutOptions = (labels: string[], colors?: string[]) => ({
    chart: { type: 'donut' as const },
    labels,
    colors: colors ?? ['#2E37A4', '#FF6384', '#00e396', '#feb019', '#775dd0'],
    legend: { position: 'bottom' as const },
  });

  const formatDateLabel = (dateStr: string) =>
    new Date(dateStr + 'T12:00:00').toLocaleDateString('es-DO', { day: '2-digit', month: 'short' });

  // ── Consultations charts ──────────────────────────────────────────────────

  const consultByDayCategories = (consultationReport?.by_day || dashboard?.admissions?.by_day || []).map(d => formatDateLabel(d.date));
  const consultByDaySeries = [{ name: 'Consultas', data: (consultationReport?.by_day || dashboard?.admissions?.by_day || []).map(d => d.count) }];

  const doctorCategories = (consultationReport?.by_doctor || []).map(d =>
    d.doctor ? `Dr. ${d.doctor.first_name} ${d.doctor.last_name}` : `Doctor #${d.doctor_id}`
  );
  const doctorSeries = [{ name: 'Consultas', data: (consultationReport?.by_doctor || []).map(d => d.total) }];

  // ── Patients charts ───────────────────────────────────────────────────────

  const genderLabels = (patientReport?.summary?.by_gender || []).map(g => g.label);
  const genderSeries = (patientReport?.summary?.by_gender || []).map(g => g.count);

  const byMonthCategories = (patientReport?.summary?.by_month || []).map(m => {
    const [y, mo] = m.month.split('-');
    return new Date(Number(y), Number(mo) - 1).toLocaleDateString('es-DO', { month: 'short', year: '2-digit' });
  });
  const byMonthSeries = [{ name: 'Pacientes nuevos', data: (patientReport?.summary?.by_month || []).map(m => m.count) }];

  // ── Financial charts ──────────────────────────────────────────────────────

  const finByDayCategories = (financialReport?.by_day || []).map(d => formatDateLabel(d.date));
  const finByDaySeries = [{ name: 'Ingresos (RD$)', data: (financialReport?.by_day || []).map(d => Number(d.total)) }];

  const finBreakdownLabels = ['Servicios', 'Medicamentos', 'Suministros', 'Honorarios'];
  const finBreakdownSeries = financialReport ? [
    financialReport.summary.services,
    financialReport.summary.medications,
    financialReport.summary.supplies,
    financialReport.summary.honoraries,
  ] : [];

  // ── Billing charts ────────────────────────────────────────────────────────

  const billingByDayCategories = (billingReport?.by_day || []).map(d => formatDateLabel(d.date));
  const billingByDaySeries = [{ name: 'Ingresos (RD$)', data: (billingReport?.by_day || []).map(d => Number(d.revenue)) }];
  const billingCountSeries = [{ name: 'Facturas', data: (billingReport?.by_day || []).map(d => Number(d.count)) }];

  const paymentMethodLabels = (billingReport?.by_payment_method || []).map(p =>
    p.payment_method === 'cash' ? 'Efectivo' :
    p.payment_method === 'card' ? 'Tarjeta' :
    p.payment_method === 'insurance' ? 'Seguro' :
    p.payment_method === 'transfer' ? 'Transferencia' :
    p.payment_method ?? 'Sin especificar'
  );
  const paymentMethodSeries = (billingReport?.by_payment_method || []).map(p => Number(p.total));

  // ── Lab charts ────────────────────────────────────────────────────────────

  const labStatusLabels = (labReport?.by_status || []).map(s => STATUS_LABELS[s.status] ?? s.status);
  const labStatusSeries = (labReport?.by_status || []).map(s => s.count);

  // ── Imaging charts ────────────────────────────────────────────────────────

  const imagingStatusLabels = (imagingReport?.by_status || []).map(s => STATUS_LABELS[s.status] ?? s.status);
  const imagingStatusSeries = (imagingReport?.by_status || []).map(s => s.count);

  const NoData = () => <EmptyState icon="fa-chart-bar" title="No hay datos en este período" />;

  return (
    <>
      {/* Breadcrumb */}
      <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Reportes y Estadísticas' }]} />
              </div>
            </div>
          </div>

          {/* Date Filter */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-3">
                  <label className="form-label fw-bold">Fecha Inicio</label>
                  <input type="date" className="form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Fecha Fin</label>
                  <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <button className="btn btn-primary" onClick={fetchReports} disabled={loading}>
                    <i className="fa fa-refresh me-1"></i> {loading ? 'Cargando...' : 'Actualizar'}
                  </button>
                </div>
                <div className="col-md-3 text-end">
                  <small className="text-muted">
                    Período: {new Date(startDate + 'T12:00:00').toLocaleDateString('es-DO')} – {new Date(endDate + 'T12:00:00').toLocaleDateString('es-DO')}
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <ul className="nav nav-tabs nav-tabs-solid mb-4" style={{ flexWrap: 'wrap' }} role="tablist">
            {TABS.map(tab => (
              <li className="nav-item" key={tab.key}>
                <button
                  className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  aria-controls={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <i className={`fa ${tab.icon} me-1`}></i> {tab.label}
                </button>
              </li>
            ))}
          </ul>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="mt-2 text-muted">Cargando reportes...</p>
            </div>
          ) : (
            <>
              {/* ── GENERAL TAB ─────────────────────────────────────────── */}
              {activeTab === 'general' && dashboard && (
                <div id="general">
                  <div className="row">
                    {[
                      { label: 'Total Pacientes',  value: dashboard.patients.total,            color: 'primary',   icon: 'fa-users' },
                      { label: 'Pacientes Nuevos', value: dashboard.patients.new,              color: 'success',   icon: 'fa-user-plus' },
                      { label: 'Consultas',        value: dashboard.consultations?.total || 0, color: 'info',      icon: 'fa-stethoscope' },
                      { label: 'Citas',            value: dashboard.appointments?.total || 0,  color: 'warning',   icon: 'fa-calendar' },
                      { label: 'Laboratorio',      value: dashboard.laboratory.total,          color: 'danger',    icon: 'fa-flask' },
                      { label: 'Imágenes',         value: dashboard.imaging.total,             color: 'secondary', icon: 'fa-image' },
                    ].map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                  </div>

                  <div className="row mb-4 mt-1">
                    <div className="col-md-6">
                      <div className="card" style={{ borderLeft: '4px solid #00e396' }}>
                        <div className="card-body d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">Ingresos del Período</small>
                            <h3 className="mb-0 text-success">{formatCurrency(dashboard.financial.revenue)}</h3>
                          </div>
                          <i className="fa fa-money fa-3x text-success" style={{ opacity: 0.2 }}></i>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card" style={{ borderLeft: '4px solid #ff6384' }}>
                        <div className="card-body d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">Descuentos del Período</small>
                            <h3 className="mb-0 text-danger">{formatCurrency(dashboard.financial.discounts)}</h3>
                          </div>
                          <i className="fa fa-percent fa-3x text-danger" style={{ opacity: 0.2 }}></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  {consultByDaySeries[0].data.length > 0 && (
                    <div className="card mb-4">
                      <div className="card-body">
                        <h5 className="card-title">Actividad Diaria (Consultas)</h5>
                        <Chart options={areaOptions(consultByDayCategories, ' consultas')} series={consultByDaySeries} type="area" height={300} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── CONSULTATIONS TAB ───────────────────────────────────── */}
              {activeTab === 'consultations' && (
                <div id="consultations">
                  <div className="row">
                    <KpiCard label="Total Consultas"   value={consultationReport?.total || 0}              color="primary" icon="fa-stethoscope" />
                    <KpiCard label="Doctores Activos"  value={consultationReport?.by_doctor?.length || 0}  color="info"    icon="fa-user-md" />
                    <KpiCard label="Días con Actividad" value={consultationReport?.by_day?.length || 0}    color="success" icon="fa-calendar-check" />
                  </div>

                  <div className="row mt-1">
                    <div className="col-md-7">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Consultas por Día</h5>
                          {consultByDaySeries[0].data.length > 0
                            ? <Chart options={areaOptions(consultByDayCategories, ' consultas')} series={consultByDaySeries} type="area" height={300} />
                            : <NoData />}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-5">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Consultas por Doctor</h5>
                          {doctorSeries[0].data.length > 0
                            ? <Chart options={barOptions(doctorCategories, '#00e396', true)} series={doctorSeries} type="bar" height={300} />
                            : <NoData />}
                        </div>
                      </div>
                    </div>
                  </div>

                  {(consultationReport?.by_doctor?.length || 0) > 0 && (
                    <div className="card mt-1">
                      <div className="card-body">
                        <h5 className="card-title">Detalle por Doctor</h5>
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>Doctor</th>
                              <th className="text-center">Consultas</th>
                              <th>% del Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {consultationReport!.by_doctor.map((d, i) => (
                              <tr key={i}>
                                <td>{d.doctor ? `Dr. ${d.doctor.first_name} ${d.doctor.last_name}` : `Doctor #${d.doctor_id}`}</td>
                                <td className="text-center"><strong>{d.total}</strong></td>
                                <td>
                                  <div className="progress" style={{ height: 20 }}>
                                    <div
                                      className="progress-bar bg-primary"
                                      style={{ width: `${(d.total / (consultationReport!.total || 1)) * 100}%` }}
                                    >
                                      {((d.total / (consultationReport!.total || 1)) * 100).toFixed(1)}%
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── PATIENTS TAB ────────────────────────────────────────── */}
              {activeTab === 'patients' && (
                <div id="patients">
                  <div className="row">
                    <KpiCard label="Total Pacientes"   value={patientReport?.summary?.total || 0}          color="primary" icon="fa-users" />
                    <KpiCard label="Nuevos en Período" value={patientReport?.summary?.new_in_period || 0}  color="success" icon="fa-user-plus" />
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body">
                          <h6 className="text-center mb-3">Distribución por Género</h6>
                          {genderSeries.length > 0
                            ? <Chart options={donutOptions(genderLabels, ['#2E37A4', '#FF6384'])} series={genderSeries} type="donut" height={200} />
                            : <NoData />}
                        </div>
                      </div>
                    </div>
                  </div>

                  {byMonthSeries[0].data.length > 0 && (
                    <div className="card mt-1">
                      <div className="card-body">
                        <h5 className="card-title">Pacientes Nuevos por Mes</h5>
                        <Chart options={barOptions(byMonthCategories, '#feb019')} series={byMonthSeries} type="bar" height={280} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── FINANCIAL TAB ───────────────────────────────────────── */}
              {activeTab === 'financial' && (
                <div id="financial">
                  <div className="row">
                    {[
                      { label: 'Cierres de Cuenta', value: financialReport?.summary?.total_closures || 0,                      color: 'primary', icon: 'fa-file-lines' },
                      { label: 'Ingresos Totales',  value: formatCurrency(financialReport?.summary?.grand_total || 0) as string, color: 'success', icon: 'fa-money' },
                      { label: 'Descuentos',        value: formatCurrency(financialReport?.summary?.discounts || 0) as string,   color: 'danger',  icon: 'fa-percent' },
                    ].map(({ label, value, color, icon }, i) => (
                      <div className="col-md-4" key={i}>
                        <div className="card text-center" style={{ borderTop: `3px solid var(--bs-${color})` }}>
                          <div className="card-body py-3">
                            <i className={`fa ${icon} fa-2x text-${color} mb-2`}></i>
                            <h4 className="mb-0">{value}</h4>
                            <small className="text-muted">{label}</small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="row mt-1">
                    <div className="col-md-8">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Ingresos por Día</h5>
                          {finByDaySeries[0].data.length > 0
                            ? <Chart options={areaOptions(finByDayCategories, ' RD$')} series={finByDaySeries} type="area" height={300} />
                            : <NoData />}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Desglose por Categoría</h5>
                          {finBreakdownSeries.some(v => v > 0)
                            ? <Chart options={donutOptions(finBreakdownLabels)} series={finBreakdownSeries} type="donut" height={280} />
                            : <NoData />}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Financial breakdown table */}
                  {financialReport && (
                    <div className="card mt-1">
                      <div className="card-body">
                        <h5 className="card-title">Resumen Financiero</h5>
                        <table className="table">
                          <tbody>
                            {[
                              { label: 'Servicios',      value: financialReport.summary.services },
                              { label: 'Medicamentos',   value: financialReport.summary.medications },
                              { label: 'Suministros',    value: financialReport.summary.supplies },
                              { label: 'Honorarios',     value: financialReport.summary.honoraries },
                            ].map((row, i) => (
                              <tr key={i}>
                                <td>{row.label}</td>
                                <td className="text-end fw-bold">{formatCurrency(row.value)}</td>
                              </tr>
                            ))}
                            <tr className="table-light">
                              <td>Descuentos</td>
                              <td className="text-end fw-bold text-danger">– {formatCurrency(financialReport.summary.discounts)}</td>
                            </tr>
                            <tr className="table-success">
                              <td><strong>Total</strong></td>
                              <td className="text-end fw-bold text-success">{formatCurrency(financialReport.summary.grand_total)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── ADMISSIONS TAB ──────────────────────────────────────── */}
              {activeTab === 'admissions' && (
                <div id="admissions">
                  <div className="row">
                    <KpiCard label="Total Admisiones" value={admissionReport?.total || 0} color="primary" icon="fa-hospital" />
                  </div>
                  {(!admissionReport || admissionReport.total === 0) && (
                    <div className="card mt-2">
                      <div className="card-body">
                        <EmptyState icon="fa-hospital" title="No hay admisiones registradas en este período" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── LABORATORY TAB ──────────────────────────────────────── */}
              {activeTab === 'laboratory' && (
                <div id="laboratory">
                  <div className="row">
                    <KpiCard label="Órdenes Totales"   value={labReport?.total_orders || 0}                   color="primary" icon="fa-flask" />
                    <KpiCard label="Validadas"          value={(labReport?.by_status || []).find(s => s.status === 'validated')?.count || 0} color="success" icon="fa-check-circle" />
                    <KpiCard label="Pendientes"         value={(labReport?.by_status || []).find(s => s.status === 'pending')?.count   || 0} color="warning" icon="fa-clock" />
                  </div>

                  <div className="row mt-1">
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Distribución por Estado</h5>
                          {labStatusSeries.length > 0 && labStatusSeries.some(v => v > 0)
                            ? <Chart options={donutOptions(labStatusLabels)} series={labStatusSeries} type="donut" height={300} />
                            : <NoData />}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Detalle por Estado</h5>
                          {labStatusSeries.length > 0
                            ? (
                              <table className="table table-striped">
                                <thead>
                                  <tr><th>Estado</th><th className="text-center">Cantidad</th></tr>
                                </thead>
                                <tbody>
                                  {labReport!.by_status.map((s, i) => (
                                    <tr key={i}>
                                      <td>{STATUS_LABELS[s.status] ?? s.status}</td>
                                      <td className="text-center"><strong>{s.count}</strong></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : <NoData />}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── BILLING TAB ─────────────────────────────────────────── */}
              {activeTab === 'billing' && (
                <div id="billing">
                  <div className="row">
                    {[
                      { label: 'Total Facturas',  value: billingReport?.summary?.total || 0,                                     color: 'primary', icon: 'fa-file-lines' },
                      { label: 'Pagadas',         value: billingReport?.summary?.paid || 0,                                      color: 'success', icon: 'fa-check-circle' },
                      { label: 'Borradores',      value: billingReport?.summary?.draft || 0,                                     color: 'warning', icon: 'fa-clock' },
                      { label: 'Canceladas',      value: billingReport?.summary?.cancelled || 0,                                  color: 'danger',  icon: 'fa-times-circle' },
                      { label: 'Ingresos',        value: formatCurrency(billingReport?.summary?.revenue || 0) as string,          color: 'success', icon: 'fa-money' },
                      { label: 'Promedio Factura', value: formatCurrency(billingReport?.summary?.avg_invoice || 0) as string,     color: 'info',    icon: 'fa-calculator' },
                    ].map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                  </div>

                  <div className="row mt-1">
                    <div className="col-md-8">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Ingresos por Día</h5>
                          {billingByDaySeries[0].data.length > 0
                            ? <Chart options={{ ...areaOptions(billingByDayCategories), tooltip: { y: { formatter: (v: number) => formatCurrency(v) } } }} series={billingByDaySeries} type="area" height={300} />
                            : <NoData />}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Métodos de Pago</h5>
                          {paymentMethodSeries.length > 0 && paymentMethodSeries.some(v => v > 0)
                            ? <Chart options={donutOptions(paymentMethodLabels, ['#2E37A4', '#00e396', '#FF6384', '#feb019'])} series={paymentMethodSeries} type="donut" height={280} />
                            : <NoData />}
                        </div>
                      </div>
                    </div>
                  </div>

                  {(billingReport?.by_doctor?.length || 0) > 0 && (
                    <div className="card mt-1">
                      <div className="card-body">
                        <h5 className="card-title">Top Doctores por Facturación</h5>
                        <div className="table-responsive">
                          <table className="table table-striped">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Doctor</th>
                                <th className="text-center">Facturas</th>
                                <th className="text-end">Ingresos</th>
                              </tr>
                            </thead>
                            <tbody>
                              {billingReport!.by_doctor.map((d, i) => (
                                <tr key={i}>
                                  <td><span className="badge bg-primary">{i + 1}</span></td>
                                  <td>Dr. {d.first_name} {d.last_name}</td>
                                  <td className="text-center"><strong>{d.total_invoices}</strong></td>
                                  <td className="text-end fw-bold text-success">{formatCurrency(d.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {billingCountSeries[0].data.length > 0 && (
                    <div className="card mt-1">
                      <div className="card-body">
                        <h5 className="card-title">Cantidad de Facturas por Día</h5>
                        <Chart options={barOptions(billingByDayCategories, '#2E37A4')} series={billingCountSeries} type="bar" height={250} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── IMAGING TAB ─────────────────────────────────────────── */}
              {activeTab === 'imaging' && (
                <div id="imaging">
                  <div className="row">
                    <KpiCard label="Órdenes Totales" value={imagingReport?.total_orders || 0}                              color="primary"   icon="fa-image" />
                    <KpiCard label="Completadas"     value={(imagingReport?.by_status || []).find(s => s.status === 'completed')?.count || 0} color="success" icon="fa-check-circle" />
                    <KpiCard label="Pendientes"      value={(imagingReport?.by_status || []).find(s => s.status === 'pending')?.count   || 0} color="warning" icon="fa-clock" />
                  </div>

                  <div className="row mt-1">
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Distribución por Estado</h5>
                          {imagingStatusSeries.length > 0 && imagingStatusSeries.some(v => v > 0)
                            ? <Chart options={donutOptions(imagingStatusLabels)} series={imagingStatusSeries} type="donut" height={300} />
                            : <NoData />}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">Detalle por Estado</h5>
                          {imagingStatusSeries.length > 0
                            ? (
                              <table className="table table-striped">
                                <thead>
                                  <tr><th>Estado</th><th className="text-center">Cantidad</th></tr>
                                </thead>
                                <tbody>
                                  {imagingReport!.by_status.map((s, i) => (
                                    <tr key={i}>
                                      <td>{STATUS_LABELS[s.status] ?? s.status}</td>
                                      <td className="text-center"><strong>{s.count}</strong></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : <NoData />}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
    </>
  );
};

export default ReportsPage;
